import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { UIButton } from "common/components";
import { useFormatDate } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { deactivateModal } from "domains/modal/actions";
import { approveKnowledge } from "domains/pinAddReject/actions";
import {
  StyledErrorContainer,
  StyledErrorMessage,
  StyledHeroImage,
  StyledHeroImagePlaceholder,
  StyledInsightAreaChip,
  StyledInsightAreaChipPlaceholder,
  StyledKnowledgeInfoContainer,
  StyledKnowledgeInfoHeading,
  StyledKnowledgeInfoText,
  StyledKnowledgeInsightAreaContainer,
  StyledPublisherImage,
  StyledRedCircleErrorIcon,
  StyledScrapedKnowledgeContainer,
  StyledURLDescriptionPlaceholder,
  StyledURLHalfDescriptionPlaceholder,
  StyledURLPhotoPlaceholder,
  StyledURLPreviewContainer,
  StyledURLPreviewTitle,
  StyledURLTitlePlaceholder,
} from "domains/pinAddReject/components/KnowledgePreview.styled";
import { getIsApproveKnowledgePending } from "domains/pinAddReject/selectors";

type KnowledgePreviewProps = { knowledgeId?: string; isLoading: boolean };

export const KnowledgePreview = ({ knowledgeId, isLoading }: KnowledgePreviewProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { getDate } = useFormatDate();
  const siteLanguage = useSelector(getSiteLanguage);
  const isPending = useSelector(getIsApproveKnowledgePending);

  const {
    data: knowledge,
    error,
    isFetching,
  } = knowledgeApiSlice.endpoints.getKnowledgeById.useQuery(
    { id: knowledgeId ?? "" },
    { skip: !knowledgeId || isLoading },
  );

  const languageNames = new Intl.DisplayNames([siteLanguage], { type: "language" });

  const handleApproveButtonClick = () => {
    if (knowledge)
      dispatch(approveKnowledge({ knowledgeId: knowledge?.id, onSuccess: () => dispatch(deactivateModal()) }));
  };

  if (isLoading || isFetching) return <PlaceholderKnowledgePreview />;

  if (!knowledge) return null;

  if (error)
    return (
      <StyledErrorContainer>
        <StyledRedCircleErrorIcon />
        <StyledErrorMessage>{translate("app.add_knowledge.preview.could_not_fetch_content")}</StyledErrorMessage>
      </StyledErrorContainer>
    );

  return (
    <>
      <StyledScrapedKnowledgeContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.url_preview")}</StyledKnowledgeInfoHeading>
        <StyledURLPreviewContainer>
          {knowledge.source?.images && (
            <StyledPublisherImage src={knowledge.source.images.thumbnail} alt={knowledge.source.name} />
          )}
          <StyledURLPreviewTitle>{knowledge.title}</StyledURLPreviewTitle>
          <StyledKnowledgeInfoText>{knowledge.summary}</StyledKnowledgeInfoText>
        </StyledURLPreviewContainer>
        <StyledKnowledgeInfoContainer>
          <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.publisher")}</StyledKnowledgeInfoHeading>
          <div>
            <StyledKnowledgeInfoText>{knowledge.publisher}</StyledKnowledgeInfoText>
            <StyledKnowledgeInfoText>{knowledge.link}</StyledKnowledgeInfoText>
          </div>
        </StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoContainer>
          <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.headline")}</StyledKnowledgeInfoHeading>
          <StyledKnowledgeInfoText>{knowledge.title}</StyledKnowledgeInfoText>
        </StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoContainer>
          <StyledKnowledgeInfoHeading>
            {translate("app.add_knowledge.preview.publishing_time")}
          </StyledKnowledgeInfoHeading>
          <StyledKnowledgeInfoText>{getDate({ date: knowledge.time })}</StyledKnowledgeInfoText>
        </StyledKnowledgeInfoContainer>
        {knowledge.created_at && (
          <StyledKnowledgeInfoContainer>
            <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.added_on")}</StyledKnowledgeInfoHeading>
            <StyledKnowledgeInfoText>{getDate({ date: knowledge.created_at })}</StyledKnowledgeInfoText>
          </StyledKnowledgeInfoContainer>
        )}
        <StyledKnowledgeInfoContainer>
          <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.language")}</StyledKnowledgeInfoHeading>
          {knowledge.language && (
            <StyledKnowledgeInfoText>{languageNames.of(knowledge.language)}</StyledKnowledgeInfoText>
          )}
        </StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoContainer>
          <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.hero_image")}</StyledKnowledgeInfoHeading>
          <StyledHeroImage src={knowledge.image} alt={knowledge.source.name} />
        </StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoContainer>
          <StyledKnowledgeInfoHeading>
            {translate("app.add_knowledge.preview.insight_areas")}
          </StyledKnowledgeInfoHeading>
          <StyledKnowledgeInsightAreaContainer>
            {knowledge.topics?.map((topic) => (
              <StyledInsightAreaChip key={topic.id}>{topic.name}</StyledInsightAreaChip>
            ))}
          </StyledKnowledgeInsightAreaContainer>
        </StyledKnowledgeInfoContainer>
      </StyledScrapedKnowledgeContainer>
      <UIButton fullwidth onClick={handleApproveButtonClick} isLoading={isPending}>
        {translate("app.add_knowledge.preview.approve")}
      </UIButton>
    </>
  );
};

const PlaceholderKnowledgePreview = () => {
  const translate = useTranslate();

  return (
    <StyledScrapedKnowledgeContainer>
      <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.url_preview")}</StyledKnowledgeInfoHeading>
      <StyledURLPreviewContainer>
        <StyledURLPhotoPlaceholder />
        <StyledURLTitlePlaceholder />
        <StyledURLDescriptionPlaceholder />
        <StyledURLDescriptionPlaceholder />
        <StyledURLHalfDescriptionPlaceholder />
      </StyledURLPreviewContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.publisher")}</StyledKnowledgeInfoHeading>
        <StyledURLDescriptionPlaceholder />
      </StyledKnowledgeInfoContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.headline")}</StyledKnowledgeInfoHeading>
        <StyledURLDescriptionPlaceholder />
      </StyledKnowledgeInfoContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>
          {translate("app.add_knowledge.preview.publishing_time")}
        </StyledKnowledgeInfoHeading>
        <StyledURLDescriptionPlaceholder />
      </StyledKnowledgeInfoContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.added_on")}</StyledKnowledgeInfoHeading>
        <StyledURLDescriptionPlaceholder />
      </StyledKnowledgeInfoContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.language")}</StyledKnowledgeInfoHeading>
        <StyledURLDescriptionPlaceholder />
      </StyledKnowledgeInfoContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.hero_image")}</StyledKnowledgeInfoHeading>
        <StyledHeroImagePlaceholder />
      </StyledKnowledgeInfoContainer>
      <StyledKnowledgeInfoContainer>
        <StyledKnowledgeInfoHeading>{translate("app.add_knowledge.preview.insight_areas")}</StyledKnowledgeInfoHeading>
        <StyledKnowledgeInsightAreaContainer>
          <StyledInsightAreaChipPlaceholder />
          <StyledInsightAreaChipPlaceholder />
          <StyledInsightAreaChipPlaceholder />
        </StyledKnowledgeInsightAreaContainer>
      </StyledKnowledgeInfoContainer>
    </StyledScrapedKnowledgeContainer>
  );
};
